import React, { useState, useEffect } from 'react';
import { adminAPI, Tenant, User } from '../services/api';
import { useAuth } from '../context/AuthContext';

const AdminTenants: React.FC = () => {
  const { refreshTenants } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [assignedUserIds, setAssignedUserIds] = useState<number[]>([]);

  // Create/Edit tenant form state
  const [tenantForm, setTenantForm] = useState({
    name: '',
    shopify_shop_name: '',
    shopify_access_token: '',
    is_active: true,
    cron_enabled: false,
    cron_time: '',
    cron_sync_start_date: '',
    gift_cards_enabled: false,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchTenants = async () => {
    try {
      const response = await adminAPI.getTenants(true); // Include inactive
      setTenants(response.data.tenants);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load tenants');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data.users.filter((u: User) => u.status === 'approved'));
    } catch (err: any) {
      console.error('Failed to load users:', err);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchUsers();
  }, []);

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      await adminAPI.createTenant({
        name: tenantForm.name,
        shopify_shop_name: tenantForm.shopify_shop_name,
        shopify_access_token: tenantForm.shopify_access_token,
        is_active: tenantForm.is_active,
      });
      setSuccessMessage('Tenant created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchTenants();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to create tenant');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenant) return;
    
    setFormError('');
    setFormLoading(true);

    try {
      const updateData: any = {
        name: tenantForm.name,
        shopify_shop_name: tenantForm.shopify_shop_name,
        is_active: tenantForm.is_active,
        cron_enabled: tenantForm.cron_enabled,
        cron_time: tenantForm.cron_time || null,
        cron_sync_start_date: tenantForm.cron_sync_start_date || null,
        gift_cards_enabled: tenantForm.gift_cards_enabled,
      };
      // Only include token if it was changed (not empty)
      if (tenantForm.shopify_access_token) {
        updateData.shopify_access_token = tenantForm.shopify_access_token;
      }
      
      await adminAPI.updateTenant(selectedTenant.id, updateData);
      setSuccessMessage('Tenant updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchTenants();
      // The navigation depends on tenant settings such as the gift card flag
      refreshTenants();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to update tenant');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTenant = async (tenantId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this tenant? This will not delete data, but users will no longer be able to access it.')) {
      return;
    }
    try {
      await adminAPI.deleteTenant(tenantId);
      setSuccessMessage('Tenant deactivated');
      fetchTenants();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to deactivate tenant');
    }
  };

  const handleReactivateTenant = async (tenant: Tenant) => {
    try {
      await adminAPI.updateTenant(tenant.id, { is_active: true });
      setSuccessMessage('Tenant reactivated');
      fetchTenants();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reactivate tenant');
    }
  };

  const openEditModal = async (tenant: Tenant) => {
    setSelectedTenant(tenant);
    // Format cron_time from "HH:MM:SS" to "HH:MM" for input
    const cronTime = tenant.cron_time ? tenant.cron_time.substring(0, 5) : '';
    // Format date for input
    const cronStartDate = tenant.cron_sync_start_date 
      ? new Date(tenant.cron_sync_start_date).toISOString().split('T')[0] 
      : '';
    setTenantForm({
      name: tenant.name,
      shopify_shop_name: tenant.shopify_shop_name,
      shopify_access_token: '', // Don't show existing token
      is_active: tenant.is_active,
      cron_enabled: tenant.cron_enabled || false,
      cron_time: cronTime,
      cron_sync_start_date: cronStartDate,
      gift_cards_enabled: tenant.gift_cards_enabled || false,
    });
    setShowEditModal(true);
  };

  const openAssignModal = async (tenant: Tenant) => {
    setSelectedTenant(tenant);
    try {
      const response = await adminAPI.getTenant(tenant.id);
      setAssignedUserIds(response.data.assignedUserIds || []);
      setShowAssignModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load tenant details');
    }
  };

  const handleToggleUserAssignment = async (userId: number) => {
    if (!selectedTenant) return;
    
    try {
      if (assignedUserIds.includes(userId)) {
        await adminAPI.unassignUserFromTenant(selectedTenant.id, userId);
        setAssignedUserIds(assignedUserIds.filter(id => id !== userId));
        setSuccessMessage('User unassigned from tenant');
      } else {
        await adminAPI.assignUserToTenant(selectedTenant.id, userId);
        setAssignedUserIds([...assignedUserIds, userId]);
        setSuccessMessage('User assigned to tenant');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user assignment');
    }
  };

  const resetForm = () => {
    setTenantForm({
      name: '',
      shopify_shop_name: '',
      shopify_access_token: '',
      is_active: true,
      cron_enabled: false,
      cron_time: '',
      cron_sync_start_date: '',
      gift_cards_enabled: false,
    });
    setSelectedTenant(null);
    setFormError('');
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`status-badge ${isActive ? 'status-reconciled' : 'status-variance'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  // Clear messages after 3 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  if (isLoading) {
    return <div className="loading">Loading tenants...</div>;
  }

  const activeTenants = tenants.filter(t => t.is_active);
  const inactiveTenants = tenants.filter(t => !t.is_active);

  return (
    <div>
      <div className="page-header">
        <h1>Tenant Management</h1>
        <p>Manage Shopify store connections and user access</p>
      </div>

      {error && <div className="error">{error}</div>}
      {successMessage && <div className="success">{successMessage}</div>}

      <div className="card">
        <div className="card-header-actions">
          <h2>Shopify Stores (Tenants)</h2>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
            Add Tenant
          </button>
        </div>

        <h3 className="section-title">Active Tenants ({activeTenants.length})</h3>
        {activeTenants.length === 0 ? (
          <p className="text-muted">No active tenants. Add a tenant to get started.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Shop Name</th>
                <th>Status</th>
                <th>Scheduled Import</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeTenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td><strong>{tenant.name}</strong></td>
                  <td>{tenant.shopify_shop_name}.myshopify.com</td>
                  <td>{getStatusBadge(tenant.is_active)}</td>
                  <td>
                    {tenant.cron_enabled ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        fontSize: 'var(--text-xs)',
                        padding: 'var(--space-1) var(--space-2)',
                        background: 'var(--color-primary-50)',
                        color: 'var(--color-primary-700)',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: 500,
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        Daily at {tenant.cron_time?.substring(0, 5) || '--:--'}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-neutral-400)', fontSize: 'var(--text-sm)' }}>
                        Disabled
                      </span>
                    )}
                  </td>
                  <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => openAssignModal(tenant)}
                      >
                        Assign Users
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEditModal(tenant)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteTenant(tenant.id)}
                      >
                        Deactivate
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {inactiveTenants.length > 0 && (
          <>
            <h3 className="section-title">Inactive Tenants ({inactiveTenants.length})</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Shop Name</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {inactiveTenants.map((tenant) => (
                  <tr key={tenant.id} className="inactive-row">
                    <td><strong>{tenant.name}</strong></td>
                    <td>{tenant.shopify_shop_name}.myshopify.com</td>
                    <td>{getStatusBadge(tenant.is_active)}</td>
                    <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => handleReactivateTenant(tenant)}
                        >
                          Reactivate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Tenant</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateTenant}>
              {formError && <div className="error">{formError}</div>}
              
              <div className="form-group">
                <label htmlFor="tenant-name">Display Name</label>
                <input
                  type="text"
                  id="tenant-name"
                  value={tenantForm.name}
                  onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                  placeholder="My Shopify Store"
                  required
                  disabled={formLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="tenant-shop">Shopify Shop Name</label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    id="tenant-shop"
                    value={tenantForm.shopify_shop_name}
                    onChange={(e) => setTenantForm({ ...tenantForm, shopify_shop_name: e.target.value })}
                    placeholder="my-store"
                    required
                    disabled={formLoading}
                  />
                  <span className="input-suffix">.myshopify.com</span>
                </div>
                <small className="form-hint">Enter only the shop name, not the full URL</small>
              </div>

              <div className="form-group">
                <label htmlFor="tenant-token">Shopify Access Token</label>
                <input
                  type="password"
                  id="tenant-token"
                  value={tenantForm.shopify_access_token}
                  onChange={(e) => setTenantForm({ ...tenantForm, shopify_access_token: e.target.value })}
                  placeholder="shpat_..."
                  required
                  disabled={formLoading}
                />
                <small className="form-hint">Your private app access token from Shopify admin</small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={tenantForm.is_active}
                    onChange={(e) => setTenantForm({ ...tenantForm, is_active: e.target.checked })}
                    disabled={formLoading}
                  />
                  <span>Active (users can access this tenant)</span>
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditModal && selectedTenant && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Tenant</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleEditTenant}>
              {formError && <div className="error">{formError}</div>}
              
              <div className="form-group">
                <label htmlFor="edit-tenant-name">Display Name</label>
                <input
                  type="text"
                  id="edit-tenant-name"
                  value={tenantForm.name}
                  onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                  required
                  disabled={formLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-tenant-shop">Shopify Shop Name</label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    id="edit-tenant-shop"
                    value={tenantForm.shopify_shop_name}
                    onChange={(e) => setTenantForm({ ...tenantForm, shopify_shop_name: e.target.value })}
                    required
                    disabled={formLoading}
                  />
                  <span className="input-suffix">.myshopify.com</span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="edit-tenant-token">Shopify Access Token</label>
                <input
                  type="password"
                  id="edit-tenant-token"
                  value={tenantForm.shopify_access_token}
                  onChange={(e) => setTenantForm({ ...tenantForm, shopify_access_token: e.target.value })}
                  placeholder="Leave empty to keep current token"
                  disabled={formLoading}
                />
                <small className="form-hint">Leave empty to keep the existing token</small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={tenantForm.is_active}
                    onChange={(e) => setTenantForm({ ...tenantForm, is_active: e.target.checked })}
                    disabled={formLoading}
                  />
                  <span>Active (users can access this tenant)</span>
                </label>
              </div>

              {/* Gift Cards Section */}
              <div style={{ 
                marginTop: 'var(--space-6)', 
                paddingTop: 'var(--space-6)', 
                borderTop: '1px solid var(--color-neutral-200)' 
              }}>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={tenantForm.gift_cards_enabled}
                      onChange={(e) => setTenantForm({ ...tenantForm, gift_cards_enabled: e.target.checked })}
                      disabled={formLoading}
                    />
                    <span>Enable gift card report</span>
                  </label>
                  <small className="form-hint">Adds the "Gift Cards (by date)" report and imports gift cards with the scheduled import. The Shopify token needs the read_gift_cards and read_gift_card_transactions scopes, plus read_customers for the customer columns of the CSV export.</small>
                </div>
              </div>

              {/* Scheduled Import Section */}
              <div style={{ 
                marginTop: 'var(--space-6)', 
                paddingTop: 'var(--space-6)', 
                borderTop: '1px solid var(--color-neutral-200)' 
              }}>
                <h3 style={{ 
                  fontSize: 'var(--text-base)', 
                  fontWeight: 600, 
                  marginBottom: 'var(--space-4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  Scheduled Import
                </h3>
                
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={tenantForm.cron_enabled}
                      onChange={(e) => setTenantForm({ ...tenantForm, cron_enabled: e.target.checked })}
                      disabled={formLoading}
                    />
                    <span>Enable automatic daily import</span>
                  </label>
                  <small className="form-hint">When enabled, orders will be automatically imported daily at the configured time</small>
                </div>

                {tenantForm.cron_enabled && (
                  <>
                    <div className="form-group">
                      <label htmlFor="edit-cron-time">Import Time (Daily)</label>
                      <input
                        type="time"
                        id="edit-cron-time"
                        value={tenantForm.cron_time}
                        onChange={(e) => setTenantForm({ ...tenantForm, cron_time: e.target.value })}
                        required={tenantForm.cron_enabled}
                        disabled={formLoading}
                      />
                      <small className="form-hint">The time each day when the import will run (server timezone)</small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-cron-start-date">Sync Start Date</label>
                      <input
                        type="date"
                        id="edit-cron-start-date"
                        value={tenantForm.cron_sync_start_date}
                        onChange={(e) => setTenantForm({ ...tenantForm, cron_sync_start_date: e.target.value })}
                        required={tenantForm.cron_enabled}
                        disabled={formLoading}
                      />
                      <small className="form-hint">Orders from this date to today will be imported each day</small>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Users Modal */}
      {showAssignModal && selectedTenant && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Users to {selectedTenant.name}</h2>
              <button className="modal-close" onClick={() => setShowAssignModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p className="text-muted">Select users who should have access to this tenant. Admin users always have access to all tenants.</p>
              
              {users.length === 0 ? (
                <p>No approved users available.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>Access</th>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={user.role === 'admin' || assignedUserIds.includes(user.id)}
                            onChange={() => handleToggleUserAssignment(user.id)}
                            disabled={user.role === 'admin'}
                          />
                        </td>
                        <td>{user.email}</td>
                        <td>{user.name || '-'}</td>
                        <td>
                          <span className={`status-badge ${user.role === 'admin' ? 'status-partial' : 'status-reconciled'}`}>
                            {user.role}
                          </span>
                          {user.role === 'admin' && <small className="text-muted"> (auto-access)</small>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowAssignModal(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTenants;

